/**
 * /dashboard/surveys/$id — HR-admin detail / editor for one survey (G16).
 *
 * Backend: GET /api/v1/surveys/{id} returns { survey, questions }.
 *   - draft: editor (add/remove questions, activate). Question list
 *     mutations are still gated server-side on draft status; UI
 *     mirrors that gate so HR doesn't see hopeful-but-broken controls.
 *   - active: shows the frozen question set + a "View results" CTA.
 *   - closed: shows the frozen question set + results link, no edits.
 *
 * Closing an active survey lives on the LIST row (alongside the
 * results affordance), keeping the detail page focused on
 * configuration. That avoids the trap of HR accidentally closing a
 * survey while reviewing its question set.
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, BarChart3, ShieldCheck } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useSurvey } from "@/hooks/use-surveys";
import { useAuth } from "@/lib/auth";
import { isHRAdmin } from "@/lib/nav";
import { PageHeader } from "@/components/shared/page-header";
import { StatusBadge } from "@/components/shared/status-badge";
import { SurveyEditor } from "@/components/surveys/survey-builder";
import {
  fmtDateTime,
  surveyTypeLabel,
} from "@/components/surveys/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/surveys/$id")({
  component: SurveyDetailRoute,
});

const DETAIL_SK_KEYS = ["sk-d1", "sk-d2"] as const;

function SurveyDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const surveyId = Number(id);
  const q = useSurvey(surveyId);
  const { user } = useAuth();
  const isHR = isHRAdmin(user?.role);

  const survey = q.data?.survey;

  return (
    <>
      <PageHeader
        title={survey?.title ?? "Survey"}
        description={
          survey
            ? `Created ${fmtDateTime(survey.created_at)}`
            : undefined
        }
        actions={
          <div className="flex gap-2">
            <Button asChild variant="outline" className="gap-1.5">
              <Link to="/app/$tenantSlug/surveys" params={{ tenantSlug }}>
                <ArrowLeft aria-hidden className="h-4 w-4" />
                Back
              </Link>
            </Button>
            {survey &&
              (survey.status === "active" || survey.status === "closed") && (
                <Button asChild className="gap-1.5">
                  <Link
                    to="/app/$tenantSlug/surveys/$id/results"
                    params={{tenantSlug, id: String(surveyId)}}
                    data-testid="survey-detail-results-link"
                  >
                    <BarChart3 aria-hidden className="h-4 w-4" />
                    View results
                  </Link>
                </Button>
              )}
          </div>
        }
      />

      {!isHR && (
        <Alert variant="destructive">
          <AlertTitle>HR-admin only</AlertTitle>
          <AlertDescription>
            Only HR managers and company admins can view survey details.
            Open <Link to="/app/$tenantSlug/me/surveys" params={{ tenantSlug }} className="font-medium text-primary underline-offset-4 hover:underline">My surveys</Link> instead.
          </AlertDescription>
        </Alert>
      )}

      {isHR && q.isLoading && (
        <div className="space-y-4">
          {DETAIL_SK_KEYS.map((k) => (
            <Card key={k}>
              <CardHeader>
                <Skeleton className="h-5 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isHR && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load survey</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {isHR && q.data && (
        <div className="space-y-6">
          <Card>
            <CardContent className="flex flex-wrap items-center gap-2 pt-6 text-xs text-muted-foreground">
              <StatusBadge status={q.data.survey.status} />
              <Badge variant="outline">
                {surveyTypeLabel(q.data.survey.survey_type)}
              </Badge>
              {q.data.survey.is_anonymous && (
                <span className="inline-flex items-center gap-1 text-primary">
                  <ShieldCheck aria-hidden className="h-3.5 w-3.5" />
                  Anonymous responses
                </span>
              )}
              <span>· Last updated {fmtDateTime(q.data.survey.updated_at)}</span>
              {q.data.survey.description && (
                <p className="mt-2 w-full text-sm text-gray-700">
                  {q.data.survey.description}
                </p>
              )}
            </CardContent>
          </Card>

          <SurveyEditor detail={q.data} />
        </div>
      )}
    </>
  );
}
